
import { createColumnHelper } from "@tanstack/react-table";
import { 
    useListDocumentTypes, 
    useCreateDocumentType, 
    useGetDocumentType, 
    useUpdateDocumentType, 
    useDeleteDocumentType 
} from "../../queries/documentRepository";
import { useMemo, useState, useEffect } from "react";
import TanStackTable from "../../components/TanStackTable";
import { ActionButton } from "../../components/Forms/ActionButton";
import Modal from "../Submissions/components/Modal";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useForm } from "react-hook-form";

const DocumentTypes = () => {
    const { data, isPending } = useListDocumentTypes();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { mutate: createDocumentType, isPending: isCreatingDocumentType } = useCreateDocumentType();
    const { mutate: updateDocumentType, isPending: isUpdatingDocumentType } = useUpdateDocumentType();
    const { mutate: deleteDocumentType, isPending: isDeletingDocumentType } = useDeleteDocumentType();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            name: '',
            description: '',
        },
    });

    const { data: documentType, refetch: refetchDocumentType } = useGetDocumentType(selectedDocumentType);

    // Handle form submission
    const onSubmit = async (data) => {
        if (isEditMode && selectedDocumentType) {
            // Update existing document type
            updateDocumentType({
                id: selectedDocumentType,
                data: data
            }, {
                onSuccess: (res) => {
                    if (res.error) {
                        toast.error("Failed to update document type");
                        return;
                    }
                    toast.success("Document type updated successfully");
                    setShowCreateModal(false);
                    setIsEditMode(false);
                    setSelectedDocumentType(null);
                    reset();
                },
                onError: (error) => {
                    const errorMessage = error?.response?.data?.message || error?.message || error || "Unknown error";
                    toast.error("Failed to update document type: " + errorMessage);
                }
            });
        } else {
            // Create new document type
            createDocumentType(data, {
                onSuccess: (res) => {
                    if (res.error) {
                        toast.error("Failed to create document type");
                        return;
                    }
                    toast.success("Document type created successfully");
                    setShowCreateModal(false);
                    reset();
                },
                onError: (error) => {
                    const errorMessage = error?.response?.data?.message || error?.message || error || "Unknown error";
                    toast.error("Failed to create document type: " + errorMessage);
                }
            });
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this document type?");
        if (!confirm) return;

        deleteDocumentType(id, {
            onSuccess: () => {
                toast.success("Document type deleted successfully");
            },
            onError: (error) => {
                console.error("Error deleting document type:", error);
                const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
                toast.error("Failed to delete document type: " + errorMessage);
            }
        });
    };

    // Handle closing modal with cleanup
    const handleCloseModal = () => {
        setShowCreateModal(false);
        setIsEditMode(false);
        setSelectedDocumentType(null);
        reset();
    };

    // Handle opening create modal
    const handleOpenCreateModal = () => {
        reset();
        setIsEditMode(false);
        setSelectedDocumentType(null);
        setShowCreateModal(true);
    };

    // Handle opening edit modal
    const handleOpenEditModal = (documentTypeId) => {
        setSelectedDocumentType(documentTypeId);
        setIsEditMode(true);
        setShowCreateModal(true);
    };

    // Effect to populate form fields when document type data is loaded for editing
    useEffect(() => {
        if (documentType && isEditMode && selectedDocumentType) {
            setValue("name", documentType.name || '');
            setValue("description", documentType.description || '');
        }
    }, [documentType, isEditMode, selectedDocumentType, setValue]);

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('name', {
            header: 'Name',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('description', {
            header: 'Description',
            cell: info => (
                <div className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            align: 'center',
            enableColumnFilter: false,
            cell: info => {
                const documentTypeItem = info.row.original;
                return (
                    <div className='flex items-center justify-center gap-2'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => handleOpenEditModal(documentTypeItem._id)}
                            title="Edit Document Type"
                        >
                            <FaEdit className="fill-primary" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            onClick={() => handleDelete(documentTypeItem._id)}
                            title="Delete Document Type"
                            disabled={isDeletingDocumentType}
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    </div>
                )
            },
        }),
    ], [handleOpenEditModal, handleDelete, isDeletingDocumentType]);

    if (isPending) {
        return <div>Loading document types...</div>;
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            <Modal isOpen={showCreateModal} onClose={handleCloseModal} title={isEditMode ? "Edit Document Type" : "Add Document Type"}>
                <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-full">
                        <h2 className="text-lg font-bold mb-4">{isEditMode ? "Edit Document Type" : "Add Document Type"}</h2>
                        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
                            <div className="flex flex-col gap-0">
                                <span>Name</span>
                                <input
                                    type="text"
                                    placeholder="Enter document type name"
                                    className="border border-navBtn rounded-md p-2"
                                    {...register("name", {
                                        required: "Document type name is required",
                                    })}
                                />
                                {errors.name && <span className="text-red">{errors.name.message}</span>}
                            </div>
                            <div className="flex flex-col gap-0">
                                <span>Description</span>
                                <textarea
                                    placeholder="Enter document type description"
                                    className="border border-navBtn rounded-md p-2 resize-none"
                                    {...register("description", {
                                        required: "Document type description is required",
                                    })}
                                    rows={5}
                                />
                                {errors.description && <span className="text-red">{errors.description.message}</span>}
                            </div>
                            <div className="flex justify-end gap-3">
                                <ActionButton
                                    variant={'reset'}
                                    text={'Cancel'}
                                    handleSubmit={handleCloseModal}
                                />
                                <ActionButton
                                    variant={'submit'}
                                    text={isEditMode ? 'Update' : 'Create'}
                                    type={'submit'}
                                    disabled={isCreatingDocumentType || isUpdatingDocumentType}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full pl-6 max-md:max-w-full">
                    <div className="w-full flex items-center justify-between mb-4">
                        <h1 className="text-lg font-bold">Document Types</h1>
                        <div>
                            <ActionButton
                                variant={'submit'}
                                text={'Add Document Type'}
                                handleSubmit={handleOpenCreateModal}
                            />
                        </div>
                    </div>
                    {data && data.length > 0 ? (
                        <TanStackTable
                            data={data}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No Document Types available
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={2000}
            />
        </main>
    );
}

export default DocumentTypes;